import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, Heart, Users, DollarSign, Mail, Handshake, Clock, Loader2 } from 'lucide-react';
import { volunteerOpportunities, organizationInfo, donationPresets } from '../mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GetInvolvedPage = () => {
  const [donationAmount, setDonationAmount] = useState('25');
  const [customAmount, setCustomAmount] = useState('');
  const [donating, setDonating] = useState(false);
  const [volunteerForm, setVolunteerForm] = useState({
    name: '',
    email: '',
    interest: '',
    availability: '',
    experience: '',
    ageConfirmed: false,
    inRecovery: ''
  });
  const [volunteerSubmitting, setVolunteerSubmitting] = useState(false);

  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, []);

  const handleVolunteerSubmit = async (e) => {
    e.preventDefault();
    if (!volunteerForm.ageConfirmed) {
      toast.error('Please confirm you are 18 years of age or older.');
      return;
    }
    setVolunteerSubmitting(true);
    try {
      const res = await axios.post(`${API}/volunteer`, volunteerForm);
      toast.success(res.data.message || 'Thank you for your interest! We will be in touch.');
      setVolunteerForm({ name: '', email: '', interest: '', availability: '', experience: '', ageConfirmed: false, inRecovery: '' });
    } catch (err) {
      toast.error('Something went wrong. Please email info@sagedrecovery.org.');
    } finally {
      setVolunteerSubmitting(false);
    }
  };

  const handleDonation = async () => {
    if (donationAmount === 'custom') {
      const val = parseFloat(customAmount);
      if (!val || val < 1) {
        toast.error('Please enter a donation amount of at least $1.');
        return;
      }
    }
    setDonating(true);
    try {
      const payload = {
        amount_id: donationAmount,
        custom_amount: donationAmount === 'custom' ? parseFloat(customAmount) : null,
        origin_url: window.location.origin
      };
      const res = await axios.post(`${API}/donations/checkout`, payload);
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('No checkout URL');
      }
    } catch (err) {
      toast.error('We could not start the donation. Please try again or email info@sagedrecovery.org.');
      setDonating(false);
    }
  };

  const displayAmount =
    donationAmount === 'custom'
      ? customAmount
        ? `$${customAmount}`
        : 'Custom Amount'
      : `$${donationAmount}`;

  return (
    <div className="pt-20">
      {/* Page Header */}
      <section className="py-16 bg-gray-50">
        <div className="container text-center">
          <h1 className="heading-1 mb-6">Get Involved</h1>
          <p className="body-large max-w-3xl mx-auto">
            We're building a foundation of recovery support for Manistee and Mason Counties.
            Whether you give, volunteer, or partner with us, you're helping us grow
            something that will change lives.
          </p>
        </div>
      </section>

      {/* Donation Section */}
      <section id="donate" className="py-16">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Donate to Our Mission</h2>
            <p className="body-large max-w-2xl mx-auto">
              Your gift helps us provide peer recovery support, build community programs, and remove
              barriers to care. Every dollar plants a seed for lasting recovery in our community.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Donation Impact */}
              <div className="bg-green-50 p-8 rounded-lg">
                <h3 className="heading-3 mb-6">Your Impact</h3>
                <div className="space-y-4">
                  {[
                    { amt: '$10', title: 'Seeds of Support', desc: 'Helps cover outreach and resource materials' },
                    { amt: '$25', title: 'Transportation Fund', desc: 'Builds toward a ride to a future appointment' },
                    { amt: '$50', title: 'Peer Coaching Fund', desc: 'Invests in future peer recovery coaching' },
                    { amt: '$100', title: 'Community Programs', desc: 'Supports building local recovery programming' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="min-w-[3.25rem] h-8 px-2 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-green-600">{item.amt}</span>
                      </div>
                      <div>
                        <p className="font-medium body-medium">{item.title}</p>
                        <p className="body-small">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Donation Form */}
              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
                <h3 className="heading-3 mb-6">Make a Donation</h3>
                <div className="space-y-6">
                  <div>
                    <label className="block body-medium font-medium mb-3">Select Amount</label>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {[...donationPresets, 'custom'].map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          data-testid={`donation-amount-${amount}`}
                          onClick={() => setDonationAmount(amount)}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            donationAmount === amount
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-gray-200 hover:border-green-300'
                          }`}
                        >
                          {amount === 'custom' ? 'Other' : `$${amount}`}
                        </button>
                      ))}
                    </div>
                    {donationAmount === 'custom' && (
                      <input
                        data-testid="donation-custom-amount"
                        type="number"
                        min="1"
                        step="1"
                        value={customAmount}
                        placeholder="Enter custom amount"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        onChange={(e) => setCustomAmount(e.target.value)}
                      />
                    )}
                  </div>

                  <div className="space-y-3">
                    <button
                      data-testid="donate-button"
                      onClick={handleDonation}
                      disabled={donating}
                      className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {donating ? <Loader2 className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />}
                      {donating ? 'Redirecting to secure checkout...' : `Donate ${displayAmount}`}
                    </button>
                    <p className="caption text-center">
                      Donations are processed securely through Stripe. You'll be redirected to complete your gift.
                    </p>
                    <p className="caption text-center" data-testid="donation-tax-note">
                      Saged Recovery and Wellness Collaborative is a 501(c)(3) public charity. EIN: 41-3518085.
                      Donations are tax-deductible to the fullest extent allowed by law.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Volunteer Opportunities */}
      <section id="volunteer" className="py-16 bg-gray-50">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Volunteer With Us</h2>
            <p className="body-large max-w-2xl mx-auto">
              We're building a community of caring volunteers — and we're{' '}
              <span className="font-semibold">actively recruiting Peer Recovery Coaches and Transporters/Drivers</span> right now.
              Tell us how you'd like to help, and we'll reach out.
            </p>
          </div>

          <div className="ai-grid mb-12">
            {volunteerOpportunities.map((opportunity, index) => (
              <div key={index} className={`bg-white p-6 rounded-lg border shadow-sm ${opportunity.activelyRecruiting ? 'border-green-400 ring-1 ring-green-200' : 'border-gray-200'}`}>
                {opportunity.activelyRecruiting && (
                  <span className="inline-block mb-3 px-3 py-1 rounded-full bg-green-600 text-white text-xs font-bold uppercase tracking-wide">
                    Now Recruiting
                  </span>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-full bg-green-100">
                    <Users className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="service-card-title">{opportunity.title}</h3>
                </div>
                <p className="service-card-description mb-4">{opportunity.description}</p>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-green-600" />
                  <span className="body-small font-medium">Typical Commitment: {opportunity.commitment}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Volunteer Application Form */}
          <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-sm border border-gray-200">
            <h3 className="heading-3 mb-6 text-center">Express Your Interest</h3>
            <form onSubmit={handleVolunteerSubmit} className="space-y-6" data-testid="volunteer-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block body-medium font-medium mb-2">Full Name *</label>
                  <input
                    data-testid="volunteer-name"
                    type="text"
                    required
                    value={volunteerForm.name}
                    onChange={(e) => setVolunteerForm({ ...volunteerForm, name: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label className="block body-medium font-medium mb-2">Email Address *</label>
                  <input
                    data-testid="volunteer-email"
                    type="email"
                    required
                    value={volunteerForm.email}
                    onChange={(e) => setVolunteerForm({ ...volunteerForm, email: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label className="block body-medium font-medium mb-2">Area of Interest *</label>
                <select
                  data-testid="volunteer-interest"
                  required
                  value={volunteerForm.interest}
                  onChange={(e) => setVolunteerForm({ ...volunteerForm, interest: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Select an area of interest</option>
                  <option value="peer-mentor">Peer Recovery Coach</option>
                  <option value="transportation">Transporter / Driver</option>
                  <option value="events">Event Coordinator</option>
                  <option value="admin">Administrative Support</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block body-medium font-medium mb-2">Availability</label>
                <textarea
                  data-testid="volunteer-availability"
                  value={volunteerForm.availability}
                  onChange={(e) => setVolunteerForm({ ...volunteerForm, availability: e.target.value })}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Days, times, and how often you're available"
                />
              </div>

              <div>
                <label className="block body-medium font-medium mb-2">Relevant Experience</label>
                <textarea
                  data-testid="volunteer-experience"
                  value={volunteerForm.experience}
                  onChange={(e) => setVolunteerForm({ ...volunteerForm, experience: e.target.value })}
                  rows={4}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Share any relevant experience, including lived recovery experience, professional background, or volunteer work"
                />
              </div>

              <div>
                <label className="block body-medium font-medium mb-2">
                  Are you personally in recovery from substance use disorder? <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <select
                  data-testid="volunteer-in-recovery"
                  value={volunteerForm.inRecovery}
                  onChange={(e) => setVolunteerForm({ ...volunteerForm, inRecovery: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Select an option</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                </select>
                <p className="caption mt-2">
                  This question is optional and used only to help us match volunteers to appropriate roles.
                  Your answer is kept confidential.
                </p>
              </div>

              <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
                <input
                  data-testid="volunteer-age-confirm"
                  type="checkbox"
                  id="ageConfirmed"
                  required
                  checked={volunteerForm.ageConfirmed}
                  onChange={(e) => setVolunteerForm({ ...volunteerForm, ageConfirmed: e.target.checked })}
                  className="mt-1 h-5 w-5 accent-green-600 flex-shrink-0"
                />
                <label htmlFor="ageConfirmed" className="body-small font-medium cursor-pointer">
                  I confirm I am 18 years of age or older. *
                </label>
              </div>

              <button
                data-testid="volunteer-submit"
                type="submit"
                disabled={volunteerSubmitting}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {volunteerSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                {volunteerSubmitting ? 'Submitting...' : 'Submit Interest'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Partnership Opportunities */}
      <section id="partner" className="py-16">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Partner With Us</h2>
            <p className="body-large max-w-2xl mx-auto">
              We welcome partnerships with organizations that share our commitment to recovery and wellness.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Healthcare Providers',
                desc: 'Partner with us on future care coordination and recovery support services.',
                items: ['Referral partnerships', 'Care coordination', 'Shared training opportunities']
              },
              {
                title: 'Local Businesses',
                desc: 'Support recovery through employment opportunities, sponsorships, and involvement.',
                items: ['Employment partnerships', 'Event sponsorship', 'Workplace wellness']
              },
              {
                title: 'Community Organizations',
                desc: 'Collaborate on community programming and resource sharing initiatives.',
                items: ['Program collaboration', 'Resource sharing', 'Community events']
              }
            ].map((p, i) => (
              <div key={i} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="service-card-title mb-3">{p.title}</h3>
                <p className="service-card-description mb-4">{p.desc}</p>
                <ul className="space-y-2 body-small">
                  {p.items.map((it, idx) => (
                    <li key={idx}>• {it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="max-w-2xl mx-auto text-center bg-green-50 p-8 rounded-lg mt-12">
            <h3 className="heading-3 mb-4">Ready to Partner With Us?</h3>
            <p className="body-medium mb-6">
              We're always looking for partners who want to strengthen recovery resources in our community.
              Reach out to start the conversation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`mailto:${organizationInfo.email}`} className="btn-primary flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email {organizationInfo.email}
              </a>
              <a href="/contact" className="btn-secondary flex items-center gap-2">
                <Handshake className="h-4 w-4" />
                Contact Form
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GetInvolvedPage;
